import client from "@/client/marketDataApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetInstrumentBookFullQueryResponse, GetInstrumentBookFullPathParams, GetInstrumentBookFull404 } from "../types/GetInstrumentBookFull";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetInstrumentBookFullClient = typeof client<GetInstrumentBookFullQueryResponse, GetInstrumentBookFull404, never>;
type GetInstrumentBookFull = {
    data: GetInstrumentBookFullQueryResponse;
    error: GetInstrumentBookFull404;
    request: never;
    pathParams: GetInstrumentBookFullPathParams;
    queryParams: never;
    headerParams: never;
    response: GetInstrumentBookFullQueryResponse;
    client: {
        parameters: Partial<Parameters<GetInstrumentBookFullClient>[0]>;
        return: Awaited<ReturnType<GetInstrumentBookFullClient>>;
    };
};
export const getInstrumentBookFullQueryKey = (clientCode: GetInstrumentBookFullPathParams["clientCode"], marketCode: GetInstrumentBookFullPathParams["marketCode"], symbol: GetInstrumentBookFullPathParams["symbol"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/instruments/:symbol/data/full", params: { clientCode: clientCode, marketCode: marketCode, symbol: symbol } }] as const;
export type GetInstrumentBookFullQueryKey = ReturnType<typeof getInstrumentBookFullQueryKey>;
export function getInstrumentBookFullQueryOptions(clientCode: GetInstrumentBookFullPathParams["clientCode"], marketCode: GetInstrumentBookFullPathParams["marketCode"], symbol: GetInstrumentBookFullPathParams["symbol"], options: GetInstrumentBookFull["client"]["parameters"] = {}) {
    const queryKey = getInstrumentBookFullQueryKey(clientCode, marketCode, symbol);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetInstrumentBookFull["data"], GetInstrumentBookFull["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/instruments/${symbol}/data/full`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Returns fundamental + full depth information for a client market's
instrument as per the given symbol. This includes all information as per
the `/fundamentals` endpoint, but also includes the full depth of the
instrument.
 * @summary Full
 * @link /api/clients/:clientCode/markets/:marketCode/instruments/:symbol/data/full
 */
export function useGetInstrumentBookFull<TData = GetInstrumentBookFull["response"], TQueryData = GetInstrumentBookFull["response"], TQueryKey extends QueryKey = GetInstrumentBookFullQueryKey>(clientCode: GetInstrumentBookFullPathParams["clientCode"], marketCode: GetInstrumentBookFullPathParams["marketCode"], symbol: GetInstrumentBookFullPathParams["symbol"], options: {
    query?: Partial<QueryObserverOptions<GetInstrumentBookFull["response"], GetInstrumentBookFull["error"], TData, TQueryData, TQueryKey>>;
    client?: GetInstrumentBookFull["client"]["parameters"];
} = {}): UseQueryResult<TData, GetInstrumentBookFull["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getInstrumentBookFullQueryKey(clientCode, marketCode, symbol);
    const query = useQuery({
        ...getInstrumentBookFullQueryOptions(clientCode, marketCode, symbol, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetInstrumentBookFull["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getInstrumentBookFullSuspenseQueryKey = (clientCode: GetInstrumentBookFullPathParams["clientCode"], marketCode: GetInstrumentBookFullPathParams["marketCode"], symbol: GetInstrumentBookFullPathParams["symbol"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/instruments/:symbol/data/full", params: { clientCode: clientCode, marketCode: marketCode, symbol: symbol } }] as const;
export type GetInstrumentBookFullSuspenseQueryKey = ReturnType<typeof getInstrumentBookFullSuspenseQueryKey>;
export function getInstrumentBookFullSuspenseQueryOptions(clientCode: GetInstrumentBookFullPathParams["clientCode"], marketCode: GetInstrumentBookFullPathParams["marketCode"], symbol: GetInstrumentBookFullPathParams["symbol"], options: GetInstrumentBookFull["client"]["parameters"] = {}) {
    const queryKey = getInstrumentBookFullSuspenseQueryKey(clientCode, marketCode, symbol);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetInstrumentBookFull["data"], GetInstrumentBookFull["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/instruments/${symbol}/data/full`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Returns fundamental + full depth information for a client market's
instrument as per the given symbol. This includes all information as per
the `/fundamentals` endpoint, but also includes the full depth of the
instrument.
 * @summary Full
 * @link /api/clients/:clientCode/markets/:marketCode/instruments/:symbol/data/full
 */
export function useGetInstrumentBookFullSuspense<TData = GetInstrumentBookFull["response"], TQueryKey extends QueryKey = GetInstrumentBookFullSuspenseQueryKey>(clientCode: GetInstrumentBookFullPathParams["clientCode"], marketCode: GetInstrumentBookFullPathParams["marketCode"], symbol: GetInstrumentBookFullPathParams["symbol"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetInstrumentBookFull["response"], GetInstrumentBookFull["error"], TData, TQueryKey>>;
    client?: GetInstrumentBookFull["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetInstrumentBookFull["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getInstrumentBookFullSuspenseQueryKey(clientCode, marketCode, symbol);
    const query = useSuspenseQuery({
        ...getInstrumentBookFullSuspenseQueryOptions(clientCode, marketCode, symbol, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetInstrumentBookFull["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}